import { useSyncExternalStore } from "react";

import type { Models } from "api/types";

/**
 * Module-level state for anonymous (logged-out) browsing.
 *
 * Deliberately import-free (apart from React and API *types*) so that
 * `src/api/index.ts`, `services/AuthService` and `pages/Root` can consult or
 * feed it without creating an import cycle through `pages/Root` / `modules`.
 *
 * The mode is derived, never set: `active = allowed && !hasAuthToken`, where
 * `allowed` is the platform's `anonymousBrowsingEnabled` flag (fed by the
 * router bootstrap and `rootLoader`) and `hasAuthToken` mirrors the bearer
 * token `AuthService` holds. Both inputs are in place before any route loader
 * runs, so the derived value is correct synchronously.
 *
 * `checkoutAllowed` is the platform's second, narrower toggle: it only means
 * anything while the mode is active, and it is what turns real pricing and a
 * checkout call to action on for a logged-out visitor.
 */

export const ANONYMOUS_USER_ID = "anonymous-browsing";

let allowed = false;
let checkoutAllowed = false;
let hasAuthToken = false;
let active = false;
let checkoutActive = false;

const storeListeners = new Set<() => void>();
const changeListeners = new Set<(active: boolean) => void>();

const recompute = () => {
  const next = allowed && !hasAuthToken;
  const nextCheckout = next && checkoutAllowed;
  if (next === active && nextCheckout === checkoutActive) return;
  const modeFlipped = next !== active;
  active = next;
  checkoutActive = nextCheckout;
  // The mode itself did not flip when only the checkout toggle moved, so the
  // cached-stub purge (an `onAnonymousBrowsingChange` listener) stays put.
  if (modeFlipped) for (const listener of changeListeners) listener(active);
  for (const listener of storeListeners) listener();
};

export const setAnonymousBrowsingAllowed = (next: boolean) => {
  allowed = next;
  recompute();
};

export const setAnonymousCheckoutAllowed = (next: boolean) => {
  checkoutAllowed = next;
  recompute();
};

export const setHasAuthToken = (next: boolean) => {
  hasAuthToken = next;
  recompute();
};

export const isAnonymousBrowsing = () => active;

/**
 * Visitors see platform pricing and a checkout call to action only when both
 * practice toggles are on: anonymous browsing itself, and anonymous checkout.
 */
export const isAnonymousCheckout = () => active && checkoutAllowed;

export const subscribe = (listener: () => void) => {
  storeListeners.add(listener);
  return () => {
    storeListeners.delete(listener);
  };
};

/** Fires on every transition of the derived mode, in both directions. */
export const onAnonymousBrowsingChange = (listener: (active: boolean) => void) => {
  changeListeners.add(listener);
  return () => {
    changeListeners.delete(listener);
  };
};

export const useIsAnonymousBrowsing = () =>
  useSyncExternalStore(subscribe, isAnonymousBrowsing, isAnonymousBrowsing);

export const useIsAnonymousCheckout = () =>
  useSyncExternalStore(subscribe, isAnonymousCheckout, isAnonymousCheckout);

/**
 * Whether prices must stay hidden for `user`. Guests never see account pricing,
 * except while anonymous checkout is on, where platform pricing is the whole
 * point of the flow.
 */
export const useHidePricing = (user?: Pick<Models<"UserModel1">, "isGuest"> | null) => {
  const anonymousCheckout = useIsAnonymousCheckout();
  return !!user?.isGuest && !anonymousCheckout;
};

/**
 * The synthetic user served for `GET /user` while browsing anonymously. It is
 * a guest (`isGuest: true`) so every existing guest branch (hidden prices,
 * hidden Discover, no personalisation) applies without further changes.
 */
export const buildAnonymousUser = (): Models<"UserModel1"> => ({
  id: ANONYMOUS_USER_ID,
  isGuest: true,
  completedProfile: true,
  hasBeenPreRegistered: false,
  repeatCashBalance: 0,
  phoneNumber: "",
  isBlocked: false,
  hasActiveDispute: false,
  hasPartialDisputePendingAdminAction: false,
  firstName: null,
  lastName: null,
  fullName: null,
  email: null,
  platformLocation: null,
});

export const isAnonymousUser = (user: Pick<Models<"UserModel1">, "id"> | null | undefined) =>
  user?.id === ANONYMOUS_USER_ID;
